/**
 * ProdutoFornecedoresLista — quem vende o quê, e por quanto (Bloco 6, 12/09/2026).
 *
 * A mesma lista nos DOIS lados: pelo produto (`produtoId`, aba no detalhe do
 * produto) e pelo fornecedor (`fornecedorId`, painel na tela de Fornecedores).
 * O que muda é só qual coluna vira seletor no formulário. Backend:
 * /api/produto-fornecedores (preferencial único por produto, fator > 0,
 * mesma unidade). Escrita exige compras:gerenciar_fornecedor.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiStar, FiTrash2 } from "react-icons/fi";

import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  definirProdutoFornecedor, listarFornecedores, listarProdutoFornecedores, removerProdutoFornecedor,
} from "../../../services/compras";
import { listarProdutos } from "../../../services/estoque";
import { formatarReaisBR } from "../../../utils/dinheiro";
import { nomeMaterial } from "../../../utils/nomeMaterial";
import ComboboxBusca from "../Solicitacoes/ComboboxBusca";
import {
  Field, Form, FormFooter, FormGrid, IconButton, InlineHint, Input, Pill,
  PrimaryButton, SecondaryButton, Select, Table, TableWrapper, TruncCell,
} from "../styles";
import { VazioInline } from "./Estados";

const UNIDADES_COMPRA = [
  ["", "Mesma do estoque"], ["UN", "Unidade"], ["CX", "Caixa"], ["PC", "Pacote"], ["KG", "Quilo"],
  ["M", "Metro"], ["M2", "m²"], ["RL", "Rolo"], ["CH", "Chapa"], ["PAR", "Par"],
];

function linhasDe(payload) {
  return Array.isArray(payload) ? payload : payload?.results || [];
}

function formDe(vinculo, { produtoId, fornecedorId }) {
  return {
    id: vinculo?.id || null,
    produto: vinculo?.produto || produtoId || "",
    fornecedor: vinculo?.fornecedor || fornecedorId || "",
    codigo_no_fornecedor: vinculo?.codigo_no_fornecedor || "",
    ultimo_preco: vinculo?.ultimo_preco ? Number(vinculo.ultimo_preco) : "",
    unidade_compra: vinculo?.unidade_compra || "",
    fator_conversao: vinculo?.fator_conversao ? Number(vinculo.fator_conversao) : 1,
    prazo_dias: vinculo?.prazo_dias ?? "",
    preferencial: vinculo?.preferencial || false,
    observacao: vinculo?.observacao || "",
  };
}

export default function ProdutoFornecedoresLista({ produtoId = null, fornecedorId = null }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();
  const podeGerenciar = isMaster || hasAllPermissions(["compras:gerenciar_fornecedor"]);
  const [vinculos, setVinculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [removendo, setRemovendo] = useState(null);
  // Opções do lado que falta: produto (quando a lista é do fornecedor) ou
  // fornecedor (quando a lista é do produto).
  const [opcoes, setOpcoes] = useState([]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const p = await listarProdutoFornecedores(request, { produto: produtoId || undefined, fornecedor: fornecedorId || undefined });
      setVinculos(linhasDe(p).filter((v) => v.ativo !== false));
    } catch {
      setVinculos([]);
    } finally {
      setLoading(false);
    }
  }, [request, produtoId, fornecedorId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (!podeGerenciar) return;
    if (produtoId) {
      listarFornecedores(request, { ativo: true, pageSize: 500 })
        .then((p) => setOpcoes(linhasDe(p).map((f) => ({ id: f.id, label: f.nome, sublabel: f.documento || "", codigo: f.documento || "" }))))
        .catch(() => setOpcoes([]));
    } else if (fornecedorId) {
      listarProdutos(request, { pageSize: 2000, ativo: true })
        .then((p) => setOpcoes(linhasDe(p).map((x) => ({ id: x.id, label: nomeMaterial(x), sublabel: x.codigo, codigo: x.codigo }))))
        .catch(() => setOpcoes([]));
    }
  }, [request, produtoId, fornecedorId, podeGerenciar]);

  const selecionado = useMemo(() => {
    if (!form) return null;
    const id = produtoId ? form.fornecedor : form.produto;
    return opcoes.find((o) => o.id === id) || null;
  }, [form, opcoes, produtoId]);

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.produto || !form.fornecedor) { toast.error(produtoId ? "Escolha o fornecedor." : "Escolha o produto."); return; }
    if (!(Number(form.fator_conversao) > 0)) { toast.error("Fator de conversão deve ser maior que zero."); return; }
    try {
      await definirProdutoFornecedor(request, {
        produto: form.produto,
        fornecedor: form.fornecedor,
        codigo_no_fornecedor: form.codigo_no_fornecedor || "",
        ultimo_preco: form.ultimo_preco === "" ? null : String(form.ultimo_preco),
        unidade_compra: form.unidade_compra || "",
        fator_conversao: String(form.fator_conversao || 1),
        prazo_dias: form.prazo_dias === "" ? null : Number(form.prazo_dias),
        preferencial: Boolean(form.preferencial),
        observacao: form.observacao || "",
        ativo: true,
      });
      toast.success("Preço por fornecedor salvo.");
      setForm(null);
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao salvar.");
    }
  };

  const remover = async () => {
    try {
      await removerProdutoFornecedor(request, removendo.id);
      toast.success("Vínculo removido.");
      setRemovendo(null);
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao remover.");
      setRemovendo(null);
    }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // Menor preço por unidade de ESTOQUE (preço ÷ fator) entre os que têm preço;
  // só faz sentido destacar quando há comparação (2+ com preço).
  const melhorId = useMemo(() => {
    const comPreco = vinculos.filter((v) => Number(v.ultimo_preco) > 0);
    if (comPreco.length < 2) return null;
    const porEstoque = (v) => Number(v.ultimo_preco) / (Number(v.fator_conversao) || 1);
    return comPreco.reduce((a, b) => (porEstoque(a) <= porEstoque(b) ? a : b)).id;
  }, [vinculos]);

  return (
    <div>
      <InlineHint>
        O preço é por unidade de <strong>compra</strong>; o fator diz quantas unidades de estoque vêm em uma de compra. A ordem de compra sugere o preço do fornecedor escolhido, e cada recebimento atualiza o último preço.
      </InlineHint>
      {podeGerenciar && !form ? (
        <div style={{ margin: "10px 0" }}>
          <SecondaryButton type="button" onClick={() => setForm(formDe(null, { produtoId, fornecedorId }))}>
            <FiPlus size={12} /> {produtoId ? "Adicionar fornecedor" : "Adicionar produto"}
          </SecondaryButton>
        </div>
      ) : null}

      {form ? (
        <Form onSubmit={salvar} style={{ marginBottom: 14 }}>
          {!form.id ? (
            <Field>
              {produtoId ? "Fornecedor *" : "Produto *"}
              <ComboboxBusca
                opcoes={opcoes}
                valor={selecionado}
                onSelecionar={(o) => setForm((p) => ({ ...p, [produtoId ? "fornecedor" : "produto"]: o ? o.id : "" }))}
                placeholder={produtoId ? "Busque pelo nome do fornecedor…" : "Busque pelo nome ou código do material…"}
                carregando={opcoes.length === 0}
              />
            </Field>
          ) : null}
          <FormGrid>
            <Field>Código no fornecedor<Input value={form.codigo_no_fornecedor} onChange={set("codigo_no_fornecedor")} maxLength={60} /></Field>
            <Field>Preço (R$, por unidade de compra)<Input type="number" step="0.0001" min="0" value={form.ultimo_preco} onChange={set("ultimo_preco")} /></Field>
          </FormGrid>
          <FormGrid>
            <Field>
              Unidade de compra
              <Select value={form.unidade_compra} onChange={set("unidade_compra")}>
                {UNIDADES_COMPRA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field>Fator (estoque por unidade de compra)<Input type="number" step="0.0001" min="0.0001" value={form.fator_conversao} onChange={set("fator_conversao")} /></Field>
          </FormGrid>
          <FormGrid>
            <Field>Prazo (dias)<Input type="number" min="0" value={form.prazo_dias} onChange={set("prazo_dias")} /></Field>
            <Field>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 22, cursor: "pointer" }}>
                <input type="checkbox" checked={form.preferencial} onChange={set("preferencial")} /> Fornecedor preferencial
              </label>
            </Field>
          </FormGrid>
          <Field>Observação<Input value={form.observacao} onChange={set("observacao")} maxLength={300} /></Field>
          <FormFooter>
            <SecondaryButton type="button" onClick={() => setForm(null)}>Cancelar</SecondaryButton>
            <PrimaryButton type="submit">{form.id ? "Salvar" : "Adicionar"}</PrimaryButton>
          </FormFooter>
        </Form>
      ) : null}

      {loading ? null : vinculos.length === 0 ? (
        <VazioInline>{produtoId ? "Nenhum fornecedor cadastrado para este material." : "Nenhum produto com preço neste fornecedor."}</VazioInline>
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: "34%" }} /><col style={{ width: "16%" }} /><col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} /><col style={{ width: "12%" }} /><col style={{ width: "12%" }} />
            </colgroup>
            <thead><tr><th>{produtoId ? "Fornecedor" : "Produto"}</th><th>Preço</th><th>Un. compra</th><th>Fator</th><th>Prazo</th><th>Ações</th></tr></thead>
            <tbody>
              {vinculos.map((v) => (
                <tr key={v.id}>
                  <td title={produtoId ? v.fornecedor_nome : v.produto_nome}>
                    <TruncCell>
                      {produtoId ? v.fornecedor_nome : `${v.produto_codigo} — ${v.produto_nome}`}
                      {v.preferencial ? <Pill $tone="info" style={{ marginLeft: 6, fontSize: "0.66rem" }}><FiStar size={9} /> preferencial</Pill> : null}
                      {v.id === melhorId ? <Pill $tone="success" style={{ marginLeft: 6, fontSize: "0.66rem" }}>melhor preço</Pill> : null}
                    </TruncCell>
                    {v.codigo_no_fornecedor ? <div style={{ fontSize: 11, color: "var(--ink-3)" }}>cód. {v.codigo_no_fornecedor}</div> : null}
                  </td>
                  <td>{v.ultimo_preco ? formatarReaisBR(v.ultimo_preco) : "—"}{v.ultimo_preco_em ? <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{new Date(v.ultimo_preco_em + "T00:00:00").toLocaleDateString("pt-BR")}</div> : null}</td>
                  <td>{v.unidade_compra || "estoque"}</td>
                  <td>{Number(v.fator_conversao)}</td>
                  <td>{v.prazo_dias ?? "—"}</td>
                  <td>
                    {podeGerenciar ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <IconButton type="button" title="Editar" onClick={() => setForm(formDe(v, { produtoId, fornecedorId }))}><FiEdit2 size={11} /></IconButton>
                        <IconButton type="button" title="Remover" onClick={() => setRemovendo(v)}><FiTrash2 size={11} /></IconButton>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      <ConfirmDialog
        open={Boolean(removendo)}
        titulo="Remover este vínculo?"
        descricao="O histórico de preço fica guardado; o fornecedor só deixa de ser sugerido para este material."
        confirmarLabel="Sim, remover"
        cancelarLabel="Não"
        onConfirmar={remover}
        onCancelar={() => setRemovendo(null)}
      />
    </div>
  );
}
